import React, { useState } from 'react';
import { AccommodationInfo } from './types';
import { useToast } from '@/hooks/use-toast';
import { useOpenAiApi } from '@/hooks/useOpenAiApi';
import { Button } from '@/components/ui/button';
import { Plus, Building, Wand2, Loader2 } from 'lucide-react';
import { AccommodationCollapsible } from './AccommodationCollapsible';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PlacesAutocompleteV1 } from '@/components/ui/places-autocomplete-v1';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { format } from 'date-fns';
import { AccommodationForm } from './AccommodationForm';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { PageSelectionModal } from './PageSelectionModal';
import { globalMoveAccommodationToPage as globalMoveAccommodationToPageFunc } from '@/hooks/useQuotationForm';

interface AccommodationManagerProps {
  pageId: string;
  accommodations: AccommodationInfo[];
  updateAccommodations: (accommodations: AccommodationInfo[]) => void;
  collapsedItems: Record<string, boolean>;
  onToggleItem: (id: string) => void;
  hideAddButton?: boolean;
  hideTitle?: boolean;
  allPages?: any[]; // All pages in the quotation
  removeAccommodationFromPage?: (pageId: string, accommodationId: string) => void;
  addAccommodationToPage?: (pageId: string, accommodation: AccommodationInfo) => void;
  globalMoveAccommodationToPage?: (sourcePageId: string, accommodationId: string, targetPageId: string) => void;
}

export function AccommodationManager({ 
  pageId, 
  accommodations, 
  updateAccommodations,
  collapsedItems,
  onToggleItem,
  hideAddButton = false,
  hideTitle = false,
  allPages = [],
  removeAccommodationFromPage,
  addAccommodationToPage,
  globalMoveAccommodationToPage,
}: AccommodationManagerProps) {
  // Adicionar log para debug
  console.log('AccommodationManager - allPages:', allPages);
  
  const { toast } = useToast();
  const { chatCompletion } = useOpenAiApi();
  const [isAccommodationImageManagerOpen, setIsAccommodationImageManagerOpen] = useState(false);
  const [currentAccommodationId, setCurrentAccommodationId] = useState<string | null>(null);
  const [isProcessingAccommodationDescription, setIsProcessingAccommodationDescription] = useState(false);
  // Add states for accommodation movement
  const [accommodationToMove, setAccommodationToMove] = useState<string | null>(null);
  const [isPageSelectionOpen, setIsPageSelectionOpen] = useState(false);

  // Inicializa os estados de colapso para todas as acomodações existentes
  React.useEffect(() => {
    if (!accommodations || accommodations.length === 0) return;
    
    // Don't automatically set any item as expanded on mount
    // This caused a loop when combined with other event listeners
    // Just initialize the state but don't trigger any toggles
  }, [pageId, accommodations]);

  // Escutar eventos de expansão de itens para colapsar as acomodações quando outro item for expandido
  React.useEffect(() => {
    const handleItemExpanded = (event: CustomEvent) => {
      const { itemType, itemId, pageId: eventPageId } = event.detail;
      
      // Se o evento for de outra página ou for para uma acomodação, ignorar
      if (eventPageId !== pageId || itemType === 'accommodation') {
        return;
      }
      
      // Colapsar todas as acomodações quando outro tipo de item for expandido
      // Just notify parent component instead of trying to auto-collapse specific items
      onToggleItem('');
    };
    
    // Adicionar o event listener
    document.addEventListener('item-expanded', handleItemExpanded as EventListener);
    
    // Remover ao desmontar o componente
    return () => {
      document.removeEventListener('item-expanded', handleItemExpanded as EventListener);
    };
  }, [pageId, onToggleItem]);

  // Função para adicionar nova acomodação
  const addAccommodation = () => {
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd");
    
    const newAccommodation: AccommodationInfo = {
      id: `accommodation-${Date.now()}`,
      name: '',
      checkInDate: '',
      checkInTime: '',
      checkOutDate: '',
      checkOutTime: '',
      roomType: '',
      description: '',
      priceNotes: '',
      address: '',
      showAddress: true,
      images: [],
      globalOrder: accommodations.length + 1  // Increment by 1 to place at the end
    };
    
    updateAccommodations([...(accommodations || []), newAccommodation]);
    
    // Notify parent component that a new item was added and should be expanded
    setTimeout(() => {
      onToggleItem(newAccommodation.id);
    }, 0);
  };

  // Função para remover uma acomodação
  const removeAccommodation = (accommodationId: string) => {
    const updatedAccommodations = accommodations.filter(
      (accommodation: AccommodationInfo) => accommodation.id !== accommodationId
    );
    updateAccommodations(updatedAccommodations);
  };

  // Função para atualizar um campo específico de uma acomodação
  const updateAccommodation = (accommodationId: string, field: keyof AccommodationInfo, value: any) => {
    let safeValue;
    
    // Special handling for coordinates to ensure they're always properly formatted numbers
    if (field === 'coordinates' && value) {
      // Ensure lat and lng are valid numbers
      const lat = typeof value.lat === 'string' ? parseFloat(value.lat) : Number(value.lat);
      const lng = typeof value.lng === 'string' ? parseFloat(value.lng) : Number(value.lng);
      
      safeValue = {
        lat: isNaN(lat) ? 0 : lat,
        lng: isNaN(lng) ? 0 : lng
      };
      
      console.log("[AccommodationManager] Normalized coordinates:", {
        original: value,
        normalized: safeValue,
        types: {
          originalLatType: typeof value.lat,
          originalLngType: typeof value.lng,
          normalizedLatType: typeof safeValue.lat,
          normalizedLngType: typeof safeValue.lng
        }
      });
    } 
    // Special handling for address to ensure it's a valid string
    else if (field === 'address') {
      // Ensure address is a string and not undefined
      safeValue = value?.toString() || '';
      console.log("[AccommodationManager] Processing address:", {
        originalValue: value,
        originalType: typeof value,
        safeValue,
        safeValueType: typeof safeValue,
        isEmpty: safeValue === '',
        accommodationId
      });
    }
    else {
      // For all other fields, use regular deep copy
      safeValue = typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value;
    }
    
    const updatedAccommodations = accommodations.map((accommodation: AccommodationInfo) => {
      if (accommodation.id === accommodationId) {
        const updated = { ...accommodation, [field]: safeValue };
        
        // Extra debugging for coordinate updates
        if (field === 'coordinates') {
          console.log('[AccommodationManager] Updated accommodation with new coordinates:', {
            accommodation: accommodation.id,
            name: accommodation.name,
            oldCoordinates: accommodation.coordinates,
            newCoordinates: safeValue,
            fullObject: updated
          });
        }
        
        // Extra debugging for address updates
        if (field === 'address') {
          console.log('[AccommodationManager] Updated accommodation with new address:', {
            accommodation: accommodation.id,
            name: accommodation.name,
            oldAddress: accommodation.address,
            newAddress: safeValue,
            addressType: typeof safeValue,
            fullObject: updated
          });
        }
        
        return updated;
      }
      return accommodation;
    });
    
    console.log(`[AccommodationManager] Updated field ${String(field)}:`, {
      accommodationId,
      fieldName: field,
      newValue: safeValue,
      valueType: typeof safeValue,
      isObject: typeof safeValue === 'object',
      hasCoordinates: field === 'coordinates',
      isAddress: field === 'address'
    });
    
    updateAccommodations(updatedAccommodations);
  };

  // Função para reordenar as acomodações (arrastar e soltar)
  const reorderAccommodations = (sourceIndex: number, destinationIndex: number) => {
    if (!accommodations || accommodations.length === 0) return;
    
    const updatedAccommodations = Array.from(accommodations);
    const [removed] = updatedAccommodations.splice(sourceIndex, 1);
    updatedAccommodations.splice(destinationIndex, 0, removed);
    
    updateAccommodations(updatedAccommodations);
  };

  // Função para lidar com o final de uma operação de arrastar e soltar
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    reorderAccommodations(result.source.index, result.destination.index);
  };

  // Função para alternar o estado de colapso de uma acomodação
  const toggleAccommodationCollapse = (accommodationId: string) => {
    // If empty ID, it's just a signal to collapse all - forward to parent component
    if (!accommodationId) {
      onToggleItem('');
      return;
    }
    
    // Se o item atual está colapsado (fechado), vamos abri-lo e fechar todos os outros
    if (collapsedItems[accommodationId]) {
      // Fechar todos os itens primeiro
      const allCollapsed = Object.keys(collapsedItems).reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {} as Record<string, boolean>);
      
      // Abrir apenas o item atual
      onToggleItem(accommodationId);
      
      // Disparar um evento para notificar outros componentes que uma acomodação foi expandida
      document.dispatchEvent(new CustomEvent('item-expanded', {
        detail: {
          itemType: 'accommodation',
          itemId: accommodationId,
          pageId: pageId
        }
      }));
    } else {
      // Se está aberto, apenas fechamos
      onToggleItem(accommodationId);
    }
  };

  // Função para abrir o gerenciador de imagens
  const openAccommodationImageManager = (accommodationId: string) => {
    setCurrentAccommodationId(accommodationId);
    setIsAccommodationImageManagerOpen(true);
  };

  // Função para processar a descrição de uma hospedagem com IA
  const handleProcessAccommodationDescription = async (accommodationId: string) => {
    if (!accommodations) return;
    
    const accommodation = accommodations.find(acc => acc.id === accommodationId);
    if (!accommodation || !accommodation.description || accommodation.description.trim() === '') {
      toast({
        title: "Descrição vazia",
        description: "Por favor, insira um texto na descrição para ser processado pela IA.",
        variant: "destructive"
      });
      return;
    }

    // Extrair o texto puro do HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = accommodation.description;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';

    if (!textContent.trim()) {
      toast({
        title: "Descrição vazia",
        description: "Por favor, insira um texto na descrição para ser processado pela IA.",
        variant: "destructive"
      });
      return;
    }

    // Guardar o texto original
    const originalDescription = accommodation.description;
    setIsProcessingAccommodationDescription(true);
    
    try {
      toast({
        title: "Processando descrição",
        description: "Enviando o texto para processamento com IA...",
      });
      
      const response = await chatCompletion(textContent);
      
      // Extrair a resposta
      const generatedContent = response.choices[0].message.content.trim();
      
      // Atualizar o campo
      updateAccommodation(accommodationId, 'description', generatedContent);
      
      toast({
        title: "Texto gerado",
        description: "A descrição foi processada com IA e atualizada com sucesso.",
      });
    } catch (error) {
      console.error('Erro ao processar descrição com IA:', error);
      
      // Restaurar o texto original
      updateAccommodation(accommodationId, 'description', originalDescription);
      
      toast({
        title: "Erro no processamento",
        description: error instanceof Error ? error.message : "Ocorreu um erro ao processar o texto com IA.",
        variant: "destructive"
      });
    } finally {
      setIsProcessingAccommodationDescription(false);
    }
  };

  // Função para atualizar imagens de uma acomodação
  const handleAccommodationImageUpload = (accommodationId: string, images: string[]) => {
    const accommodation = accommodations?.find(acc => acc.id === accommodationId);
    if (!accommodation) return;
    
    const currentImages = accommodation.images || [];
    updateAccommodation(accommodationId, 'images', [...currentImages, ...images]);
  };

  // Função para atualizar múltiplos campos de uma acomodação de uma vez
  const batchUpdateAccommodation = (accommodationId: string, updates: Partial<AccommodationInfo>) => {
    console.log('[AccommodationManager] Batch updating accommodation:', {
      accommodationId,
      updates
    });
    
    const updatedAccommodations = accommodations.map((accommodation: AccommodationInfo) => {
      if (accommodation.id === accommodationId) {
        // Create a deep copy of the accommodation to avoid reference issues
        const updated = JSON.parse(JSON.stringify({
          ...accommodation,
          ...updates
        }));
        
        // Ensure coordinates are handled properly
        if (updates.coordinates) {
          updated.coordinates = {
            lat: Number(updates.coordinates.lat),
            lng: Number(updates.coordinates.lng)
          };
        }
        
        console.log('[AccommodationManager] Updated accommodation in batch:', {
          accommodationId,
          name: accommodation.name,
          updatedKeys: Object.keys(updates),
          address: {
            before: accommodation.address,
            after: updated.address
          },
          coordinates: {
            before: accommodation.coordinates,
            after: updated.coordinates
          }
        });
        
        return updated;
      }
      return accommodation;
    });
    
    updateAccommodations(updatedAccommodations);
  };

  // Função para duplicar uma acomodação
  const duplicateAccommodation = (sourcePageId: string, accommodationId: string) => {
    const accommodationToDuplicate = accommodations.find(
      (accommodation: AccommodationInfo) => accommodation.id === accommodationId
    );
    
    if (!accommodationToDuplicate) return;
    
    // Deep clone the accommodation object
    const duplicatedAccommodation: AccommodationInfo = JSON.parse(JSON.stringify(accommodationToDuplicate));
    
    // Update properties for the duplicate
    duplicatedAccommodation.id = `accommodation-${Date.now()}`;
    duplicatedAccommodation.name = `${duplicatedAccommodation.name || 'Nova Hospedagem'} (Cópia)`;
    duplicatedAccommodation.globalOrder = accommodations.length + 1; // Place at the end
    
    // Add the duplicated accommodation to the list
    updateAccommodations([...accommodations, duplicatedAccommodation]);
    
    // Notify parent component that a new item was added and should be expanded
    setTimeout(() => {
      onToggleItem(duplicatedAccommodation.id);
    }, 0);
    
    toast({
      title: "Hospedagem duplicada",
      description: "A hospedagem foi duplicada com sucesso.",
    });
  };

  // Function to initiate accommodation move process
  const handleInitiateAccommodationMove = (accommodationId: string) => {
    // Calculate available pages (excluding current page)
    const availablePages = allPages.filter(page => page.id !== pageId);
    
    if (availablePages.length === 0) {
      toast({
        title: "Operação não disponível",
        description: "É necessário ter mais de uma página disponível para mover a hospedagem.",
      });
      return;
    }
    
    // Set the accommodation to move and open the page selection modal
    setAccommodationToMove(accommodationId);
    setIsPageSelectionOpen(true);
  };

  // Handle page selection for accommodation movement
  const handleMoveToPage = (targetPageId: string) => {
    console.log("[AccommodationManager] handleMoveToPage called with targetPageId:", targetPageId);
    
    if (!accommodationToMove) {
      console.log("[AccommodationManager] No accommodation to move, aborting");
      return;
    }
    
    // Find the accommodation to move
    const accommodation = accommodations.find(a => a.id === accommodationToMove);
    console.log("[AccommodationManager] Accommodation found:", accommodation);
    
    if (!accommodation) {
      console.log("[AccommodationManager] Accommodation not found in list, aborting");
      return;
    }
    
    // Close the modal
    setIsPageSelectionOpen(false);
    
    // Debug log for globalMoveAccommodationToPage availability
    console.log("[AccommodationManager] globalMoveAccommodationToPage:", { 
      exists: !!globalMoveAccommodationToPage,
      type: typeof globalMoveAccommodationToPage,
      isFunction: typeof globalMoveAccommodationToPage === 'function',
      hasAllRequiredParams: pageId && accommodationToMove && targetPageId
    });
    
    try {
      // Use the globalMoveAccommodationToPage function directly with all required parameters
      if (typeof globalMoveAccommodationToPage === 'function') {
        console.log("[AccommodationManager] Calling globalMoveAccommodationToPage prop with:", {
          sourcePageId: pageId,
          accommodationId: accommodationToMove,
          targetPageId
        });
        
        // Call the function with source page ID, accommodation ID, and target page ID
        globalMoveAccommodationToPage(pageId, accommodationToMove, targetPageId);
        
        toast({
          title: "Hospedagem movida",
          description: `A hospedagem foi movida para a página selecionada.`,
        });
      } else if (typeof globalMoveAccommodationToPageFunc === 'function') {
        // Try using the global function as a fallback
        console.log("[AccommodationManager] Calling globalMoveAccommodationToPageFunc (global import) with:", {
          sourcePageId: pageId,
          accommodationId: accommodationToMove,
          targetPageId
        });
        
        globalMoveAccommodationToPageFunc(pageId, accommodationToMove, targetPageId);
        
        toast({
          title: "Hospedagem movida",
          description: `A hospedagem foi movida para a página selecionada usando a função global.`,
        });
      } else {
        console.log("[AccommodationManager] No accommodation move function available:", {
          propFunction: typeof globalMoveAccommodationToPage,
          globalFunction: typeof globalMoveAccommodationToPageFunc
        });
        
        // Fallback: just show a toast that we can't move the accommodation
        toast({
          title: "Funcionalidade não disponível",
          description: "A função de mover hospedagem para outra página não está disponível.",
        });
      }
    } catch (error) {
      console.error("[AccommodationManager] Error in globalMoveAccommodationToPage:", error);
      toast({
        title: "Erro ao mover hospedagem",
        description: "Ocorreu um erro ao tentar mover a hospedagem: " + (error instanceof Error ? error.message : String(error)),
        variant: "destructive"
      });
    }
    
    // Clear the accommodation to move
    setAccommodationToMove(null);
  };

  const imageManagerAccommodation = currentAccommodationId
    ? accommodations.find((a) => a.id === currentAccommodationId)
    : undefined;

  return (
    <div className="space-y-4">
      {!hideTitle && (
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium">Hospedagens</h3>
          {!hideAddButton && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-white text-[#087a83] border-[#087a83] hover:bg-[#087a83] hover:text-white"
              onClick={addAccommodation}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Hospedagem
            </Button>
          )}
        </div>
      )}

      {hideTitle && !hideAddButton && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-white text-[#087a83] border-[#087a83] hover:bg-[#087a83] hover:text-white"
            onClick={addAccommodation}
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Hospedagem
          </Button>
        </div>
      )}

      {(!accommodations || accommodations.length === 0) ? (
        <div className="p-6 border border-dashed rounded-md text-center">
          <Building className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">Você ainda não adicionou nenhuma hospedagem a esta cotação.</p>
          {!hideAddButton && (
            <Button 
              variant="outline"
              onClick={addAccommodation}
              className="bg-white hover:bg-gray-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Hospedagem
            </Button>
          )}
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={`accommodations-${pageId}`} type="ACCOMMODATION">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-4"
              >
                {accommodations.map((accommodation: AccommodationInfo, index: number) => (
                  <Draggable
                    key={accommodation.id}
                    draggableId={accommodation.id}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <AccommodationForm
                          key={accommodation.id}
                          accommodation={accommodation}
                          index={index}
                          pageId={pageId}
                          updateAccommodation={(pageId, accommodationId, field, value) => {
                            updateAccommodation(accommodationId, field, value);
                          }}
                          batchUpdateAccommodation={batchUpdateAccommodation}
                          deleteAccommodation={(pageId, accommodationId) => {
                            removeAccommodation(accommodationId);
                          }}
                          duplicateAccommodation={duplicateAccommodation}
                          moveAccommodationToPage={handleInitiateAccommodationMove}
                          isCollapsed={collapsedItems[accommodation.id] !== false}
                          onToggleCollapse={() => toggleAccommodationCollapse(accommodation.id)}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
      
      {/* Image Manager Modal */}
      {isAccommodationImageManagerOpen && currentAccommodationId && imageManagerAccommodation && (
        <ImageManagerModal
          isOpen={isAccommodationImageManagerOpen}
          onClose={() => setIsAccommodationImageManagerOpen(false)}
          images={imageManagerAccommodation.images || []}
          onImagesChange={(images) => {
            updateAccommodation(currentAccommodationId, 'images', images);
          }}
          contextId={currentAccommodationId}
          contextType="accommodation"
          bookingStayParams={{
            checkin: imageManagerAccommodation.checkInDate,
            checkout: imageManagerAccommodation.checkOutDate,
          }}
        />
      )}
      
      {/* Page Selection Modal */}
      {isPageSelectionOpen && (
        <PageSelectionModal
          isOpen={isPageSelectionOpen}
          onClose={() => setIsPageSelectionOpen(false)}
          pages={allPages}
          onSelectPage={handleMoveToPage}
          currentPageId={pageId}
          title="Selecionar página de destino"
        />
      )}
    </div>
  );
} 